/**
 * Formatare ro-RO. Toate formatarile trec prin `Intl` — separatorii (`1.234.567,89`), numele
 * zilelor si formele de plural romanesti nu se construiesc de mana.
 */

import { DEFAULT_MINOR_SCALE, RATE_SCALE, type Minor, type RateE6 } from './money.ts';

const LOCALE = 'ro-RO';
const TZ = 'Europe/Bucharest';

// Formatterele `Intl` sunt scumpe la construire si ieftine la reutilizare.
const cache = new Map<string, Intl.NumberFormat>();
function numberFormat(min: number, max: number): Intl.NumberFormat {
  const key = `${min}:${max}`;
  let fmt = cache.get(key);
  if (!fmt) {
    fmt = new Intl.NumberFormat(LOCALE, {
      minimumFractionDigits: min,
      maximumFractionDigits: max,
      useGrouping: true,
    });
    cache.set(key, fmt);
  }
  return fmt;
}

/** `250000000` bani => `"2.500.000,00"`. Fara simbol de moneda — acela e sufix separat in UI. */
export function formatMinor(
  amountMinor: Minor | null | undefined,
  options: { minorScale?: number; decimals?: 0 | 2; blankForNull?: boolean } = {},
): string {
  if (amountMinor === null || amountMinor === undefined) {
    return options.blankForNull ? '' : '—';
  }
  const scale = options.minorScale ?? DEFAULT_MINOR_SCALE;
  const decimals = options.decimals ?? 2;
  return numberFormat(decimals, decimals).format(amountMinor / scale);
}

/**
 * Desparte suma in parte intreaga si zecimale, ca UI-ul sa poata estompa zecimalele.
 * Reproduce lizibilitatea formatului `#,##0` din Excel fara a ascunde banii.
 */
export function splitMinor(
  amountMinor: Minor,
  minorScale = DEFAULT_MINOR_SCALE,
): { sign: '' | '-'; integer: string; fraction: string; separator: string } {
  const negative = amountMinor < 0;
  const text = numberFormat(2, 2).format(Math.abs(amountMinor) / minorScale);
  // separatorul zecimal ro este `,` dar il citim din Intl in loc sa-l presupunem
  const parts = numberFormat(2, 2).formatToParts(1.5);
  const separator = parts.find((p) => p.type === 'decimal')?.value ?? ',';
  const idx = text.lastIndexOf(separator);
  if (idx === -1) return { sign: negative ? '-' : '', integer: text, fraction: '', separator };
  return {
    sign: negative ? '-' : '',
    integer: text.slice(0, idx),
    fraction: text.slice(idx + separator.length),
    separator,
  };
}

/** Curs cu 4 zecimale: `5240900` => `"5,2409"`. */
export function formatRate(rateE6: RateE6 | null | undefined, decimals = 4): string {
  if (rateE6 === null || rateE6 === undefined) return '—';
  return numberFormat(decimals, decimals).format(rateE6 / RATE_SCALE);
}

/** Pentru axe de grafic: `1.250.000` => `"1,25 mil."`. Doar pe axe, niciodata in tabele. */
export function formatCompact(amountMinor: Minor, minorScale = DEFAULT_MINOR_SCALE): string {
  const value = amountMinor / minorScale;
  const abs = Math.abs(value);
  if (abs >= 1_000_000) return `${numberFormat(0, 2).format(value / 1_000_000)} mil.`;
  if (abs >= 1_000) return `${numberFormat(0, 0).format(value / 1_000)} mii`;
  return numberFormat(0, 0).format(value);
}

/**
 * Negativele la print primesc si paranteze, nu doar minus si culoare — culoarea nu supravietuieste
 * unei imprimante alb-negru, iar minusul singur se pierde la scanare.
 */
export function formatMinorAccounting(amountMinor: Minor, minorScale = DEFAULT_MINOR_SCALE): string {
  const text = numberFormat(2, 2).format(Math.abs(amountMinor) / minorScale);
  return amountMinor < 0 ? `(${text})` : text;
}

// ---------------------------------------------------------------------------
// Date
// ---------------------------------------------------------------------------

const dayNameFmt = new Intl.DateTimeFormat(LOCALE, { weekday: 'long', timeZone: TZ });
const dateTimeFmt = new Intl.DateTimeFormat(LOCALE, {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  timeZone: TZ,
});

/** `'2026-08-17'` => `'17.08.2026'`. Format pur textual, fara `Date`, deci fara riscuri de fus. */
export function formatIsoDate(iso: string): string {
  const [y, m, d] = iso.split('-');
  if (!y || !m || !d) return iso;
  return `${d}.${m}.${y}`;
}

/** `'17.08.2026'` sau `'17.08.26'` => `'2026-08-17'`. `null` daca nu se poate parsa. */
export function parseRoDate(input: string): string | null {
  const text = input.trim();
  const m = /^(\d{1,2})[.\-/](\d{1,2})[.\-/](\d{2,4})$/.exec(text);
  if (!m) {
    // acceptam si ISO direct
    return /^\d{4}-\d{2}-\d{2}$/.test(text) ? text : null;
  }
  const day = Number(m[1]);
  const month = Number(m[2]);
  let year = Number(m[3]);
  if (year < 100) year += year < 70 ? 2000 : 1900;
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  const iso = `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  // verificam ca data exista chiar (31.02 nu exista)
  const check = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(check.getTime()) || check.getUTCDate() !== day) return null;
  return iso;
}

/** `'2026-08-17'` => `'Luni'`, cu majuscula, exact ca etichetele din foaie. */
export function weekdayName(iso: string): string {
  const name = dayNameFmt.format(new Date(`${iso}T12:00:00Z`));
  return name.charAt(0).toLocaleUpperCase(LOCALE) + name.slice(1);
}

/** `'2026-08-17'` => `'Luni 17.08.2026'` — formatul etichetelor de coloana din Excel. */
export function formatDayLabel(iso: string): string {
  return `${weekdayName(iso)} ${formatIsoDate(iso)}`;
}

/** Timestamp epoch-ms => `'17.08.2026, 14:32'` in ora Bucurestiului. */
export function formatTimestamp(epochMs: number): string {
  return dateTimeFmt.format(new Date(epochMs));
}

/** Data de azi in calendarul Bucurestiului, ca `'YYYY-MM-DD'`. NU folosi `new Date()` direct. */
export function todayInBucharest(now: number = Date.now()): string {
  // en-CA da direct ISO (YYYY-MM-DD), fara a construi noi stringul
  return new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: TZ,
  }).format(new Date(now));
}

/** Ora curenta (0-23) in Bucuresti — pentru fereastra de publicare BNR si cronul de 18:00. */
export function hourInBucharest(now: number = Date.now()): number {
  return Number(
    new Intl.DateTimeFormat('en-GB', { hour: '2-digit', hour12: false, timeZone: TZ }).format(
      new Date(now),
    ),
  );
}

/** Anul si saptamana ISO pentru o data. Saptamanile din Excel („week 34") sunt ISO. */
export function isoWeek(iso: string): { year: number; week: number } {
  const date = new Date(`${iso}T00:00:00Z`);
  const day = date.getUTCDay() || 7; // luni=1 .. duminica=7
  date.setUTCDate(date.getUTCDate() + 4 - day); // joia din aceeasi saptamana ISO
  const year = date.getUTCFullYear();
  const jan1 = Date.UTC(year, 0, 1);
  const week = Math.ceil(((date.getTime() - jan1) / 86_400_000 + 1) / 7);
  return { year, week };
}

/** Adauga zile calendaristice la o data ISO, fara a atinge fusul orar. */
export function addDays(iso: string, days: number): string {
  const date = new Date(`${iso}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

/** Diferenta in zile calendaristice, `b - a`. */
export function daysBetween(a: string, b: string): number {
  const ms = Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`);
  return Math.round(ms / 86_400_000);
}

// ---------------------------------------------------------------------------
// Plural romanesc
// ---------------------------------------------------------------------------

const pluralRules = new Intl.PluralRules(LOCALE);

/**
 * Romana are forma `few` si insereaza „de" de la 20 in sus: `1 plata` / `3 plati` / `21 de plati`.
 * Logica `+ "e"` scrisa de mana ar fi vizibil greasita.
 */
export function plural(count: number, one: string, few: string, many?: string): string {
  const form = pluralRules.select(count);
  const noun = form === 'one' ? one : form === 'few' ? few : (many ?? `de ${few}`);
  return `${numberFormat(0, 0).format(count)} ${noun}`;
}

// ---------------------------------------------------------------------------
// Comparare / cautare insensibila la diacritice
// ---------------------------------------------------------------------------

const collator = new Intl.Collator(LOCALE, { sensitivity: 'base', numeric: true });

export function compareRo(a: string, b: string): number {
  return collator.compare(a, b);
}

/**
 * Pliaza diacriticele si cazul, pentru indexare si potrivire.
 *
 * NFD descompune si `ș`/`ț` romanesti (U+0219/U+021B => litera + virgula dedesubt) si varianta
 * greasit tastata cu sedila (`ş`/`ţ`), deci stergerea semnelor combinate (`\p{M}`) le rezolva pe
 * toate fara cazuri speciale.
 */
export function foldRo(text: string): string {
  return text
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .trim();
}
